import { FormEvent, useEffect, useState } from "react";
import type { LeaveRequest, LeaveRequestInput, LeaveRequestPage } from "@cyh/contracts";
import { CalendarPlus, ExternalLink, Eye, RefreshCw, RotateCcw, ShieldCheck, X } from "lucide-react";
import { api } from "../api";
import { useLanguage } from "../i18n";
import { dateBounds, EMPTY_DATE_FILTER, RecordDateFilter, RecordPagination, type DateFilterValue } from "./RecordListControls";

const PAGE_SIZE = 20;
const leaveTypes: Array<{ value: string; label: [string, string] }> = [
  { value: "事假", label: ["事假", "Personal leave"] },
  { value: "年假", label: ["年假", "Annual leave"] },
  { value: "病假", label: ["病假", "Sick leave"] },
  { value: "调休", label: ["调休", "Time off in lieu"] },
  { value: "婚假", label: ["婚假", "Marriage leave"] },
  { value: "产假", label: ["产假", "Maternity leave"] },
  { value: "陪产假", label: ["陪产假", "Paternity leave"] },
  { value: "丧假", label: ["丧假", "Bereavement leave"] },
  { value: "其他", label: ["其他", "Other"] }
];

const statusCopy: Record<LeaveRequest["status"], [string, string]> = {
  PENDING_SYNC: ["正在发送钉钉", "Sending to DingTalk"],
  PENDING: ["审批中", "Pending approval"],
  APPROVED: ["已通过", "Approved"],
  REJECTED: ["已拒绝", "Rejected"],
  CANCELLED: ["已撤销", "Cancelled"],
  SYNC_FAILED: ["同步失败", "Sync failed"]
};

const syncCopy: Record<LeaveRequest["syncStatus"], [string, string]> = {
  QUEUED: ["等待同步", "Queued"],
  SYNCING: ["同步中", "Syncing"],
  SYNCED: ["已同步", "Synced"],
  FAILED: ["同步失败", "Failed"],
  DISABLED: ["未启用同步", "Sync disabled"]
};

export function LeavePanel({ permissions, currentUserId, onChanged }: { permissions: string[]; currentUserId: string; onChanged: () => Promise<void> }) {
  const { language, t } = useLanguage();
  const canCreate = permissions.includes("*") || permissions.includes("leave.create");
  const canApprove = permissions.includes("*") || permissions.includes("leave.approve");
  const [records, setRecords] = useState<LeaveRequestPage | null>(null);
  const [dateFilter, setDateFilter] = useState<DateFilterValue>(EMPTY_DATE_FILTER);
  const [page, setPage] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [creating, setCreating] = useState(false);
  const [detail, setDetail] = useState<LeaveRequest | null>(null);

  useEffect(() => setPage(1), [dateFilter.mode, dateFilter.from, dateFilter.to]);

  useEffect(() => {
    let cancelled = false;
    const bounds = dateBounds(dateFilter);
    setLoading(true); setError("");
    api.leaveRequests({ page, pageSize: PAGE_SIZE, dateFrom: bounds.from, dateTo: bounds.to })
      .then((result) => { if (!cancelled) setRecords(result); })
      .catch((cause) => { if (!cancelled) setError(cause instanceof Error ? cause.message : t("请假记录加载失败", "Could not load leave requests")); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page, dateFilter.mode, dateFilter.from, dateFilter.to, reloadKey, t]);

  const dateTime = (value: string) => new Intl.DateTimeFormat(language === "en" ? "en-GB" : "zh-CN", {
    year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false
  }).format(new Date(value));

  async function created(record: LeaveRequest) {
    setCreating(false);
    setNotice(t(`请假申请已创建，当前状态：${statusCopy[record.status][0]}`, `Leave request created. Current status: ${statusCopy[record.status][1]}`));
    setPage(1); setReloadKey((value) => value + 1);
    await onChanged();
  }

  async function revoked(record: LeaveRequest) {
    setDetail(record);
    setNotice(t("请假申请已撤销，OA 与钉钉状态正在保持一致。", "The leave request was revoked. OA and DingTalk are now consistent."));
    setReloadKey((value) => value + 1);
    await onChanged();
  }

  return <div className="operations-page leave-page">
    <section className="leave-intro">
      <span><ShieldCheck /></span>
      <div><h2>{t("OA 与钉钉审批已连接", "OA and DingTalk approval are connected")}</h2><p>{t("申请提交后会自动进入钉钉审批；OA 记录、同步结果和审批状态均保留审计。", "Submitted requests are sent to DingTalk automatically. OA records, synchronization results, and approval outcomes remain audited.")}</p></div>
      {canCreate && <button className="primary-button" onClick={() => { setNotice(""); setCreating(true); }}><CalendarPlus />{t("新建请假", "New leave request")}</button>}
    </section>

    <div className="ops-toolbar leave-toolbar">
      <RecordDateFilter value={dateFilter} onChange={setDateFilter} />
      <button type="button" disabled={loading} onClick={() => setReloadKey((value) => value + 1)}><RefreshCw />{t("刷新", "Refresh")}</button>
    </div>
    {notice && <div className="success-bar">{notice}</div>}
    {error && <div className="error-bar">{error}</div>}

    <section className="ops-card">
      <div className="ops-card-title"><div><h2>{canApprove ? t("请假审批记录", "Leave approval records") : t("我的请假记录", "My leave requests")}</h2><p>{t(`共 ${records?.total ?? 0} 条当前账号可见记录`, `${records?.total ?? 0} record(s) visible to this account`)}</p></div></div>
      {loading && !records ? <div className="leave-loading">{t("正在加载请假记录…", "Loading leave requests…")}</div> : records?.items.length ? <>
        <div className="admin-table-wrap"><table className="admin-table leave-table"><thead><tr><th>{t("申请人", "Applicant")}</th><th>{t("请假类型", "Type")}</th><th>{t("请假时间", "Period")}</th><th>{t("审批状态", "Approval")}</th><th>{t("钉钉同步", "DingTalk sync")}</th><th>{t("申请时间", "Created")}</th><th /></tr></thead><tbody>{records.items.map((record) => <tr key={record.id}>
          <td><strong>{record.employeeName}</strong><small>{record.reason}</small></td>
          <td>{record.leaveType}</td>
          <td><span className="leave-period">{dateTime(record.startAt)}<b>→</b>{dateTime(record.endAt)}</span></td>
          <td><span className={`leave-status ${record.status.toLowerCase()}`}>{t(...statusCopy[record.status])}</span></td>
          <td><span className={`leave-sync ${record.syncStatus.toLowerCase()}`}>{t(...syncCopy[record.syncStatus])}</span>{record.syncError && <small className="leave-sync-error">{record.syncError}</small>}</td>
          <td>{dateTime(record.createdAt)}</td>
          <td><button className="leave-detail-button" onClick={() => setDetail(record)}><Eye />{t("详情", "Details")}</button></td>
        </tr>)}</tbody></table></div>
        <RecordPagination page={records.page} pageCount={records.pageCount} total={records.total} loading={loading} onPageChange={setPage} />
      </> : <div className="leave-empty"><CalendarPlus /><h3>{t("暂无请假记录", "No leave requests")}</h3><p>{canCreate ? t("点击“新建请假”提交第一条申请。", "Select “New leave request” to submit the first request.") : t("当前账号范围内没有可见记录。", "There are no records in this account's data scope.")}</p></div>}
    </section>
    {creating && <CreateLeaveDialog onClose={() => setCreating(false)} onCreated={created} />}
    {detail && <LeaveDetailDialog record={detail} canRevoke={(canCreate || permissions.includes("*")) && detail.employeeId === currentUserId} onClose={() => setDetail(null)} onRevoked={revoked} />}
  </div>;
}

function LeaveDetailDialog({ record, canRevoke, onClose, onRevoked }: { record: LeaveRequest; canRevoke: boolean; onClose: () => void; onRevoked: (record: LeaveRequest) => Promise<void> }) {
  const { language, t } = useLanguage();
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const revocableStatus = ["PENDING_SYNC", "PENDING", "SYNC_FAILED"].includes(record.status);
  const syncing = record.status === "PENDING_SYNC" && record.syncStatus === "SYNCING";
  const canSubmitRevoke = canRevoke && revocableStatus && !syncing;
  const dateTime = (value: string) => new Intl.DateTimeFormat(language === "en" ? "en-GB" : "zh-CN", {
    year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false
  }).format(new Date(value));
  const hours = Math.max(0, (new Date(record.endAt).getTime() - new Date(record.startAt).getTime()) / 3_600_000);
  const duration = Number.isInteger(hours) ? String(hours) : hours.toFixed(1);

  async function revoke() {
    const normalizedReason = reason.trim();
    if (normalizedReason.length < 2) { setError(t("请填写至少 2 个字符的撤销原因", "Enter at least 2 characters for the revocation reason")); return; }
    setBusy(true); setError("");
    try { await onRevoked(await api.revokeLeaveRequest(record.id, record.version, normalizedReason)); setConfirming(false); }
    catch (cause) { setError(cause instanceof Error ? cause.message : t("撤销申请失败", "Could not revoke the request")); }
    finally { setBusy(false); }
  }

  return <div className="modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onClose(); }}><div className="admin-modal leave-detail-modal" role="dialog" aria-modal="true" aria-labelledby="leave-detail-title">
    <div className="modal-heading"><h3 id="leave-detail-title">{t(`请假详情 · ${record.employeeName}`, `Leave details · ${record.employeeName}`)}</h3><button type="button" aria-label={t("关闭", "Close")} disabled={busy} onClick={onClose}><X /></button></div>
    <div className="leave-detail-content">
      {error && <div className="error-bar">{error}</div>}
      <div className="leave-detail-status"><span className={`leave-status ${record.status.toLowerCase()}`}>{t(...statusCopy[record.status])}</span><span className={`leave-sync ${record.syncStatus.toLowerCase()}`}>{t(...syncCopy[record.syncStatus])}</span></div>
      <dl className="leave-detail-grid">
        <div><dt>{t("申请人", "Applicant")}</dt><dd>{record.employeeName}</dd></div>
        <div><dt>{t("请假类型", "Leave type")}</dt><dd>{record.leaveType}</dd></div>
        <div><dt>{t("开始时间", "Start time")}</dt><dd>{dateTime(record.startAt)}</dd></div>
        <div><dt>{t("结束时间", "End time")}</dt><dd>{dateTime(record.endAt)}</dd></div>
        <div><dt>{t("时长", "Duration")}</dt><dd>{t(`${duration} 小时`, `${duration} hour(s)`)}</dd></div>
        <div><dt>{t("申请时间", "Created")}</dt><dd>{dateTime(record.createdAt)}</dd></div>
        <div className="full"><dt>{t("请假事由", "Reason")}</dt><dd>{record.reason}</dd></div>
        {record.syncError && <div className="full error"><dt>{t("同步错误", "Sync error")}</dt><dd>{record.syncError}</dd></div>}
        {record.dingtalkProcessInstanceId && <div className="full"><dt>{t("钉钉审批实例", "DingTalk approval instance")}</dt><dd className="leave-process-id">{record.dingtalkProcessInstanceId}</dd></div>}
      </dl>
      {record.dingtalkApprovalUrl?.startsWith("https://") && <a className="leave-approval-link" href={record.dingtalkApprovalUrl} target="_blank" rel="noreferrer"><ExternalLink />{t("在钉钉中查看审批", "Open approval in DingTalk")}</a>}
      {syncing && canRevoke && <div className="leave-revoke-tip">{t("申请正在发送到钉钉，请稍候刷新后再撤销。", "The request is being sent to DingTalk. Refresh shortly before revoking.")}</div>}
      {confirming && <div className="leave-revoke-box"><label>{t("撤销原因", "Revocation reason")}<textarea rows={3} minLength={2} maxLength={500} value={reason} onChange={(event) => setReason(event.target.value)} placeholder={t("请说明撤销原因，该内容会写入操作审计并同步给钉钉", "Explain why this request is being revoked. It will be audited and sent to DingTalk.")} /></label></div>}
    </div>
    <div className="modal-actions"><button type="button" disabled={busy} onClick={onClose}>{t("关闭", "Close")}</button>{canSubmitRevoke && !confirming && <button type="button" className="danger-button" onClick={() => { setError(""); setConfirming(true); }}><RotateCcw />{t("撤销申请", "Revoke request")}</button>}{canSubmitRevoke && confirming && <><button type="button" disabled={busy} onClick={() => { setConfirming(false); setError(""); }}>{t("返回", "Back")}</button><button type="button" className="danger-button" disabled={busy || reason.trim().length < 2} onClick={() => void revoke()}>{busy ? t("撤销中…", "Revoking…") : t("确认撤销", "Confirm revoke")}</button></>}</div>
  </div></div>;
}

function CreateLeaveDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (record: LeaveRequest) => Promise<void> }) {
  const { language, t } = useLanguage();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const leaveType = String(form.get("leaveType") ?? "").trim();
    const startLocal = String(form.get("startAt") ?? "");
    const endLocal = String(form.get("endAt") ?? "");
    const reason = String(form.get("reason") ?? "").trim();
    const start = new Date(startLocal);
    const end = new Date(endLocal);
    if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime())) { setError(t("请选择有效的开始和结束时间", "Select valid start and end times")); return; }
    if (end <= start) { setError(t("结束时间必须晚于开始时间", "End time must be later than start time")); return; }
    const formatter = new Intl.DateTimeFormat(language === "en" ? "en-GB" : "zh-CN", { dateStyle: "medium", timeStyle: "short" });
    if (!window.confirm(t(`确认提交${leaveType}申请？\n${formatter.format(start)} 至 ${formatter.format(end)}\n提交后将发送到钉钉审批。`, `Submit this ${leaveType} request?\n${formatter.format(start)} to ${formatter.format(end)}\nIt will be sent to DingTalk for approval.`))) return;
    const input: LeaveRequestInput = { leaveType, startAt: start.toISOString(), endAt: end.toISOString(), reason };
    setBusy(true); setError("");
    try { await onCreated(await api.createLeaveRequest(input)); }
    catch (cause) { setError(cause instanceof Error ? cause.message : t("请假申请提交失败", "Could not submit the leave request")); }
    finally { setBusy(false); }
  }

  return <div className="modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onClose(); }}><div className="admin-modal leave-modal" role="dialog" aria-modal="true" aria-labelledby="leave-dialog-title">
    <div className="modal-heading"><h3 id="leave-dialog-title">{t("新建请假申请", "New leave request")}</h3><button type="button" aria-label={t("关闭", "Close")} disabled={busy} onClick={onClose}><X /></button></div>
    <form className="admin-form leave-form" onSubmit={submit}>
      <div className="leave-form-tip full-field"><ShieldCheck /><p>{t("请核对时间和事由。确认提交后会创建正式 OA 记录，并发送到钉钉审批。", "Review the dates and reason. Confirmation creates an official OA record and sends it to DingTalk.")}</p></div>
      {error && <div className="error-bar full-field">{error}</div>}
      <label>{t("请假类型", "Leave type")}<select name="leaveType" required defaultValue="事假">{leaveTypes.map((type) => <option key={type.value} value={type.value}>{t(...type.label)}</option>)}</select></label>
      <label>{t("开始时间", "Start time")}<input name="startAt" type="datetime-local" required /></label>
      <label>{t("结束时间", "End time")}<input name="endAt" type="datetime-local" required /></label>
      <label className="full-field">{t("请假事由", "Reason")}<textarea name="reason" required minLength={2} maxLength={500} rows={4} placeholder={t("请填写真实、必要的请假事由", "Enter the necessary reason for leave")} /></label>
      <div className="modal-actions"><button type="button" disabled={busy} onClick={onClose}>{t("取消", "Cancel")}</button><button type="submit" className="primary-button" disabled={busy}>{busy ? t("提交中…", "Submitting…") : t("核对并提交", "Review & submit")}</button></div>
    </form>
  </div></div>;
}
